import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { AxiosError } from 'axios';
import { login } from '../lib/api';
import { storeTokens } from '../lib/auth';
import { LOGIN_TEMPLATE } from '../templates/loginTemplate';

export default function LoginPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => LOGIN_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return;
    }

    const form = root.querySelector<HTMLFormElement>('#login-form');
    const emailInput = root.querySelector<HTMLInputElement>('#email');
    const passwordInput = root.querySelector<HTMLInputElement>('#password');
    const submitButton = root.querySelector<HTMLButtonElement>('#login-submit');
    const submitLabel = root.querySelector<HTMLElement>('#login-submit-label');
    const submitSpinner = root.querySelector<HTMLElement>('#login-spinner');
    const passwordToggle = root.querySelector<HTMLButtonElement>('#password-toggle');
    const passwordToggleIcon = root.querySelector<HTMLElement>('#password-toggle-icon');
    const globalError = root.querySelector<HTMLElement>('#login-error');
    const globalErrorMessage = root.querySelector<HTMLElement>('#login-error-message');
    const globalErrorClose = root.querySelector<HTMLButtonElement>('#login-error-close');
    const emailError = root.querySelector<HTMLElement>('#email-error');
    const emailErrorIcon = root.querySelector<HTMLElement>('#email-error-icon');
    const passwordError = root.querySelector<HTMLElement>('#password-error');

    if (
      !form ||
      !emailInput ||
      !passwordInput ||
      !submitButton ||
      !submitLabel ||
      !submitSpinner ||
      !passwordToggle ||
      !passwordToggleIcon ||
      !globalError ||
      !globalErrorMessage ||
      !globalErrorClose ||
      !emailError ||
      !emailErrorIcon ||
      !passwordError
    ) {
      return;
    }

    const emailBaseClass =
      'w-full h-12 px-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all placeholder:text-slate-400';
    const passwordBaseClass =
      'w-full h-12 px-4 pr-11 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all';
    const errorBorderClass = ' border-red-300 dark:border-red-900/50';

    const hideGlobalError = () => {
      globalError.classList.add('hidden');
      globalErrorMessage.textContent = '';
    };

    const showGlobalError = (message: string) => {
      globalErrorMessage.textContent = message;
      globalError.classList.remove('hidden');
    };

    const setFieldError = (field: 'email' | 'password', message: string) => {
      if (field === 'email') {
        emailInput.className = emailBaseClass + errorBorderClass;
        emailError.textContent = message;
        emailError.classList.remove('hidden');
        emailErrorIcon.classList.remove('hidden');
        emailErrorIcon.classList.add('flex');
      } else {
        passwordInput.className = passwordBaseClass + errorBorderClass;
        passwordError.textContent = message;
        passwordError.classList.remove('hidden');
      }
    };

    const clearFieldError = (field: 'email' | 'password') => {
      if (field === 'email') {
        emailInput.className = emailBaseClass;
        emailError.textContent = '';
        emailError.classList.add('hidden');
        emailErrorIcon.classList.add('hidden');
        emailErrorIcon.classList.remove('flex');
      } else {
        passwordInput.className = passwordBaseClass;
        passwordError.textContent = '';
        passwordError.classList.add('hidden');
      }
    };

    const validate = (): boolean => {
      hideGlobalError();
      clearFieldError('email');
      clearFieldError('password');

      let isValid = true;
      const email = emailInput.value.trim();
      const password = passwordInput.value;

      if (!email) {
        setFieldError('email', 'Email is required.');
        isValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setFieldError('email', 'Please enter a valid email address.');
        isValid = false;
      }

      if (!password) {
        setFieldError('password', 'Password is required.');
        isValid = false;
      }

      return isValid;
    };

    const setLoading = (loading: boolean) => {
      submitButton.disabled = loading;
      if (loading) {
        submitSpinner.classList.remove('hidden');
        submitLabel.textContent = 'Signing in...';
      } else {
        submitSpinner.classList.add('hidden');
        submitLabel.textContent = 'Sign In';
      }
    };

    const onEmailInput = () => clearFieldError('email');
    const onPasswordInput = () => clearFieldError('password');

    const onPasswordToggle = () => {
      const isPassword = passwordInput.type === 'password';
      passwordInput.type = isPassword ? 'text' : 'password';
      passwordToggleIcon.textContent = isPassword ? 'visibility_off' : 'visibility';
      passwordToggle.setAttribute('aria-label', isPassword ? 'Hide password' : 'Show password');
    };

    const onSubmit = async (event: Event) => {
      event.preventDefault();
      if (!validate()) {
        return;
      }

      setLoading(true);

      try {
        const response = await login({
          email: emailInput.value.trim(),
          password: passwordInput.value,
        });
        storeTokens(response.access, response.refresh);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const axiosError = err as AxiosError<{ error?: { message?: string } }>;
        const message =
          axiosError.response?.data?.error?.message ||
          'Login failed. Check email/password and try again.';
        showGlobalError(message);
      } finally {
        setLoading(false);
      }
    };

    emailInput.className = emailBaseClass;
    passwordInput.className = passwordBaseClass;

    form.setAttribute('novalidate', 'novalidate');
    form.addEventListener('submit', onSubmit);
    emailInput.addEventListener('input', onEmailInput);
    passwordInput.addEventListener('input', onPasswordInput);
    passwordToggle.addEventListener('click', onPasswordToggle);
    globalErrorClose.addEventListener('click', hideGlobalError);

    return () => {
      form.removeEventListener('submit', onSubmit);
      emailInput.removeEventListener('input', onEmailInput);
      passwordInput.removeEventListener('input', onPasswordInput);
      passwordToggle.removeEventListener('click', onPasswordToggle);
      globalErrorClose.removeEventListener('click', hideGlobalError);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Admin Login</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 dark:bg-background-dark dark:text-slate-100 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
